import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';
import { showError } from '../../utils/notification';
import { orderSignatureService, OrderSignature } from '../../services/orderSignatureService';
import { ncaLayerService, SignatureResult } from '../../services/ncaLayerService';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types/auth';

// Модель заказа из БД
interface OrderRecord {
    id: number;
    orderId: string;
    oneCGuid: string;
    status: string;
    productGuid: string;
    productCode: string;
    productName: string;
    quantity: number;
    unitPrice: number;
    uom: string;
    itemStatus: string;
    totalAmount: number;
    requestedDeliveryDate: string;
    confirmedDeliveryDate: string;
    deliveryAddress: string;
    warehouse: string;
    paymentTerms: string;
    moderatorName: string;
    notes: string;
    createdAt: string;
    updatedAt: string;
    sentToOneCAt: string;
    receivedFromOneCAt: string;
}

// Группированный заказ для отображения
interface GroupedOrder {
    orderId: string;
    status: 'DRAFT' | 'PENDING' | 'APPROVAL' | 'APPROVED' | 'REJECTED';
    totalAmount: number;
    items: OrderItem[];
    oneCGuid?: string;
    moderatorName?: string;
    warehouse?: string;
    confirmedDeliveryDate?: string;
    requestedDeliveryDate?: string;
    deliveryAddress?: string;
    paymentTerms?: string;
    notes?: string;
    createdAt: string;
    updatedAt?: string;
}

interface OrderItem {
    id: number;
    productName: string;
    productCode: string;
    quantity: number;
    unitPrice: number;
    totalPrice: number;
    uom: string;
    itemStatus?: string;
}

export const OrderDetailPage: React.FC = () => {
    const { orderId } = useParams<{ orderId: string }>();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [order, setOrder] = useState<GroupedOrder | null>(null);
    const [loading, setLoading] = useState(true);
    
    // Состояния для подписания
    const [orderSignature, setOrderSignature] = useState<OrderSignature | null>(null);
    const [loadingSignature, setLoadingSignature] = useState(false);
    const [signing, setSigning] = useState(false);
    const [loadingInvoice, setLoadingInvoice] = useState(false);
    const [ncaLayerStatus, setNcaLayerStatus] = useState<'checking' | 'available' | 'unavailable'>('checking');
    const [certificates, setCertificates] = useState<string[]>([]);
    const [selectedCertificate, setSelectedCertificate] = useState<string>('');

    useEffect(() => {
        if (orderId) {
            loadOrderDetails();
            checkNcaLayer();
        }
    }, [orderId]);

    useEffect(() => {
        if (order && order.status === 'APPROVAL') {
            loadOrderSignature();
        }
    }, [order]);

    const loadOrderDetails = async () => {
        try {
            setLoading(true);
            const response = await api.orders.getOrderById(orderId!);
            
            if (response.success && response.data && response.data.length > 0) {
                const groupedOrder = groupOrderRecords(response.data);
                setOrder(groupedOrder);
            } else {
                showError('Ошибка', response.message || 'Заказ не найден');
                navigate('/client/orders');
            }
        } catch (error) {
            console.error('Error loading order details:', error);
            showError('Ошибка', 'Произошла ошибка при загрузке деталей заказа');
            navigate('/client/orders');
        } finally {
            setLoading(false);
        }
    };

    const groupOrderRecords = (records: OrderRecord[]): GroupedOrder => {
        const firstRecord = records[0];
        
        return {
            orderId: firstRecord.orderId,
            status: firstRecord.status as any,
            totalAmount: records.reduce((sum, r) => sum + (r.totalAmount || 0), 0),
            items: records.map(r => ({
                id: r.id,
                productName: r.productName,
                productCode: r.productCode,
                quantity: r.quantity,
                unitPrice: r.unitPrice || 0,
                totalPrice: r.totalAmount || 0,
                uom: r.uom,
                itemStatus: r.itemStatus
            })),
            oneCGuid: firstRecord.oneCGuid,
            moderatorName: firstRecord.moderatorName,
            warehouse: firstRecord.warehouse,
            confirmedDeliveryDate: firstRecord.confirmedDeliveryDate,
            requestedDeliveryDate: firstRecord.requestedDeliveryDate,
            deliveryAddress: firstRecord.deliveryAddress,
            paymentTerms: firstRecord.paymentTerms,
            notes: firstRecord.notes,
            createdAt: firstRecord.createdAt,
            updatedAt: firstRecord.updatedAt
        };
    };

    const loadOrderSignature = async () => {
        if (!orderId) return;

        try {
            setLoadingSignature(true);
            const signature = await orderSignatureService.getOrderSignatureByOrderId(orderId);
            console.log('📋 OrderSignature loaded:', signature);
            console.log('📋 isSigned:', signature?.isSigned);
            console.log('📋 status:', signature?.status);
            setOrderSignature(signature);
        } catch (error: any) {
            console.error('Ошибка загрузки заявки на подписание:', error);
            // Заявка может еще не быть создана, это нормально
        } finally {
            setLoadingSignature(false);
        }
    };

    const checkNcaLayer = async () => {
        try {
            setNcaLayerStatus('checking');
            const isAvailable = await ncaLayerService.isAvailable();
            setNcaLayerStatus(isAvailable ? 'available' : 'unavailable');

            if (isAvailable) {
                const certs = await ncaLayerService.getCertificates();
                const certPaths = certs.map(cert => cert.path || cert.alias || 'PKCS12');
                setCertificates(certPaths);
                if (certPaths.length > 0) {
                    setSelectedCertificate(certPaths[0]);
                }
            }
        } catch (error) {
            console.error('Ошибка проверки NCALayer:', error);
            setNcaLayerStatus('unavailable');
        }
    };

    const handleGenerateSignature = async () => {
        if (!orderId) return;

        try {
            setLoadingSignature(true);
            toast.loading('Генерация заявки на подписание...', { id: 'generate' });
            
            // Создаем заявку на подписание через API
            const response = await orderSignatureService.createOrderSignature(orderId);
            setOrderSignature(response);
            
            toast.success('Заявка на подписание создана!', { id: 'generate' });
        } catch (error: any) {
            console.error('Ошибка генерации заявки:', error);
            toast.error(error.message || 'Ошибка генерации заявки', { id: 'generate' });
        } finally {
            setLoadingSignature(false);
        }
    };

    const handleDownloadPdf = async () => {
        if (!orderSignature) return;

        try {
            toast.loading('Скачивание заявки...', { id: 'download' });
            await orderSignatureService.downloadOrderSignaturePdf(orderSignature.id, orderSignature.orderId);
            toast.success('Заявка скачана!', { id: 'download' });
        } catch (error: any) {
            console.error('Ошибка скачивания заявки:', error);
            toast.error(error.message || 'Ошибка скачивания заявки', { id: 'download' });
        }
    };

    const handleDownloadInvoice = async () => {
        if (!orderSignature) return;

        try {
            toast.loading('Скачивание счёта...', { id: 'download-invoice' });
            await orderSignatureService.downloadInvoice(orderSignature.id, orderSignature.orderId);
            toast.success('Счёт скачан!', { id: 'download-invoice' });
        } catch (error: any) {
            console.error('Ошибка скачивания счёта:', error);
            toast.error(error.message || 'Ошибка скачивания счёта', { id: 'download-invoice' });
        }
    };

    const handleRefetchInvoice = async () => {
        if (!orderSignature) return;

        try {
            setLoadingInvoice(true);
            toast.loading('Запрос счёта из 1С...', { id: 'refetch-invoice' });
            await orderSignatureService.refetchInvoice(orderSignature.id);
            
            // Перезагружаем данные заявки
            const updatedSignature = await orderSignatureService.getOrderSignature(orderSignature.id);
            setOrderSignature(updatedSignature);
            
            toast.success('Счёт успешно получен!', { id: 'refetch-invoice' });
        } catch (error: any) {
            console.error('Ошибка запроса счёта:', error);
            toast.error(error.message || 'Ошибка запроса счёта', { id: 'refetch-invoice' });
        } finally {
            setLoadingInvoice(false);
        }
    };

    const handleSignOrder = async () => {
        if (!orderSignature) return;

        try {
            setSigning(true);

            if (ncaLayerStatus !== 'available') {
                toast.error('NCALayer недоступен. Установите NCALayer и запустите его.');
                return;
            }

            const certPath = selectedCertificate || 'PKCS12';

            const orderData = JSON.stringify({
                orderSignatureId: orderSignature.id,
                orderId: orderSignature.orderId,
                companyBin: orderSignature.companyBin,
                timestamp: new Date().toISOString()
            });

            toast.loading('Подписание заявки...', { id: 'signing' });

            const signatureResult: SignatureResult = await ncaLayerService.signData(
                orderData,
                certPath
            );

            const isBuyer = user?.role === UserRole.CLIENT;
            const signRequest = {
                signatureData: signatureResult.signature,
                certificateData: signatureResult.certificate,
                signerName: signatureResult.signerInfo.commonName,
                organizationName: signatureResult.signerInfo.organization,
            };

            const signedOrder = isBuyer
                ? await orderSignatureService.signByBuyer(orderSignature.id, signRequest)
                : await orderSignatureService.signBySupplier(orderSignature.id, signRequest);

            toast.success('Заявка успешно подписана!', { id: 'signing' });
            setOrderSignature(signedOrder);

        } catch (error: any) {
            console.error('Ошибка подписания заявки:', error);
            toast.error(error.message || 'Ошибка подписания заявки', { id: 'signing' });
        } finally {
            setSigning(false);
        }
    };

    const getStatusColor = (status: GroupedOrder['status']) => {
        const statusColors = {
            DRAFT: 'bg-gray-100 text-gray-700 border-gray-300',
            PENDING: 'bg-yellow-100 text-yellow-700 border-yellow-300',
            APPROVAL: 'bg-purple-100 text-purple-700 border-purple-300',
            APPROVED: 'bg-green-100 text-green-700 border-green-300',
            REJECTED: 'bg-red-100 text-red-700 border-red-300'
        };
        return statusColors[status] || 'bg-gray-100 text-gray-700 border-gray-300';
    };

    const getStatusText = (status: GroupedOrder['status']) => {
        const statusTexts = {
            DRAFT: 'Черновик',
            PENDING: 'Ожидает обработки',
            APPROVAL: 'На согласовании',
            APPROVED: 'Одобрена',
            REJECTED: 'Отклонена'
        };
        return statusTexts[status] || status;
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('ru-RU', {
            style: 'currency',
            currency: 'KZT',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString?: string) => {
        if (!dateString) return 'Не указано';
        return new Date(dateString).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="space-y-6">
                <div className="text-center py-12">
                    <h2 className="text-xl font-semibold text-gray-900">Заказ не найден</h2>
                    <button
                        onClick={() => navigate('/client/orders')}
                        className="mt-4 btn-primary"
                    >
                        Вернуться к списку заказов
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <button
                        onClick={() => navigate('/client/orders')}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Заявка {order.orderId}</h1>
                        <p className="text-gray-600 mt-1">от {formatDate(order.createdAt)}</p>
                    </div>
                </div>
                <span className={`px-4 py-2 text-sm font-medium rounded-full border ${getStatusColor(order.status)}`}>
                    {getStatusText(order.status)}
                </span>
            </div>

            {/* Order Summary Card */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
                    <h2 className="text-lg font-semibold text-gray-900">Информация о заявке</h2>
                </div>
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="text-sm font-medium text-gray-600">Номер заявки</label>
                            <p className="mt-1 text-base text-gray-900 font-semibold">{order.orderId}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Общая сумма</label>
                            <p className="mt-1 text-base text-gray-900 font-semibold">{formatCurrency(order.totalAmount)}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-600">Дата создания</label>
                            <p className="mt-1 text-base text-gray-900">{formatDateTime(order.createdAt)}</p>
                        </div>
                        {order.requestedDeliveryDate && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Запрошенная дата доставки</label>
                                <p className="mt-1 text-base text-gray-900">{formatDate(order.requestedDeliveryDate)}</p>
                            </div>
                        )}
                        {order.confirmedDeliveryDate && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Подтвержденная дата доставки</label>
                                <p className="mt-1 text-base text-gray-900 font-semibold text-green-600">{formatDate(order.confirmedDeliveryDate)}</p>
                            </div>
                        )}
                        {order.warehouse && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Склад</label>
                                <p className="mt-1 text-base text-gray-900">{order.warehouse}</p>
                            </div>
                        )}
                        {order.moderatorName && (
                            <div>
                                <label className="text-sm font-medium text-gray-600">Модератор</label>
                                <p className="mt-1 text-base text-gray-900">{order.moderatorName}</p>
                            </div>
                        )}
                        {order.deliveryAddress && (
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-600">Адрес доставки</label>
                                <p className="mt-1 text-base text-gray-900">{order.deliveryAddress}</p>
                            </div>
                        )}
                        {order.paymentTerms && (
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-600">Условия оплаты</label>
                                <p className="mt-1 text-base text-gray-900">{order.paymentTerms}</p>
                            </div>
                        )}
                        {order.notes && (
                            <div className="md:col-span-2">
                                <label className="text-sm font-medium text-gray-600">Примечания</label>
                                <p className="mt-1 text-base text-gray-900">{order.notes}</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Order Signature Section - только для статуса APPROVAL */}
            {order.status === 'APPROVAL' && (
                <>
                    {/* Предварительный просмотр заявки */}
                    <div className="bg-white shadow rounded-lg mb-8">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-medium text-gray-900">
                                {orderSignature ? 'Заявка на подписание' : 'Создание заявки'}
                            </h3>
                        </div>
                        <div className="px-6 py-4">
                            {loadingSignature ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                                </div>
                            ) : orderSignature ? (
                                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                                    <div className="text-gray-400 mb-4">
                                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </div>
                                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                                        Договор готов к подписанию
                                    </h4>
                                    <p className="text-gray-500 mb-4">
                                        Договор поставки сгенерирован и готов к подписанию электронной подписью
                                    </p>
                                    <button
                                        onClick={handleDownloadPdf}
                                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg w-full"
                                    >
                                        Скачать договор (PDF)
                                    </button>
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <p className="text-gray-600 mb-4">Заявка на подписание еще не создана</p>
                                    <button
                                        onClick={handleGenerateSignature}
                                        disabled={loadingSignature}
                                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-purple-700 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all disabled:opacity-50 flex items-center justify-center space-x-2 mx-auto"
                                    >
                                        {loadingSignature ? (
                                            <>
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                                <span>Генерация...</span>
                                            </>
                                        ) : (
                                            <>
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                                <span>Сгенерировать заявку на подписание</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Электронная подпись */}
                    {orderSignature && !orderSignature.isSignedByBuyer && (
                        <div className="bg-white shadow rounded-lg mb-8">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900">Электронная подпись</h3>
                            </div>
                            <div className="px-6 py-4">
                                {/* Статус NCALayer */}
                                <div className="mb-6">
                                    <div className="flex items-center">
                                        <div className={`w-3 h-3 rounded-full mr-3 ${
                                            ncaLayerStatus === 'available' ? 'bg-green-500' :
                                            ncaLayerStatus === 'unavailable' ? 'bg-red-500' : 'bg-yellow-500'
                                        }`}></div>
                                        <span className="text-sm font-medium text-gray-700">
                                            NCALayer: {
                                                ncaLayerStatus === 'available' ? 'Доступен' :
                                                ncaLayerStatus === 'unavailable' ? 'Недоступен' : 'Проверка...'
                                            }
                                        </span>
                                    </div>
                                    {ncaLayerStatus === 'unavailable' && (
                                        <div className="mt-2">
                                            <p className="text-sm text-red-600 mb-2">
                                                Установите и запустите NCALayer для подписания документов
                                            </p>
                                            <button
                                                onClick={checkNcaLayer}
                                                className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                                            >
                                                Проверить снова
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Выбор сертификата */}
                                {ncaLayerStatus === 'available' && (
                                    <div className="space-y-4">
                                        {certificates.length > 0 ? (
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Выберите сертификат
                                                </label>
                                                <select
                                                    value={selectedCertificate}
                                                    onChange={(e) => setSelectedCertificate(e.target.value)}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    {certificates.map((cert, index) => (
                                                        <option key={index} value={cert}>
                                                            {cert}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        ) : (
                                            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                                                Сертификаты не найдены. Убедитесь, что в NCALayer установлены сертификаты ЭЦП.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Инструкции */}
                    {orderSignature && (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">
                                        Инструкции по работе с договором
                                    </h3>
                                    <div className="mt-2 text-sm text-blue-700">
                                        <ul className="list-disc list-inside space-y-1">
                                            {!orderSignature.isSignedByBuyer ? (
                                                <>
                                                    <li>Убедитесь, что у вас установлен NCALayer</li>
                                                    <li>Проверьте действительность вашего сертификата ЭЦП</li>
                                                    <li>Скачайте договор и внимательно ознакомьтесь с его содержанием</li>
                                                    <li>Нажмите "Подписать договор" для начала процесса подписания</li>
                                                </>
                                            ) : (
                                                <>
                                                    <li>Договор успешно подписан</li>
                                                    <li>Вы можете скачать подписанный договор в формате PDF</li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Кнопка подписания */}
                    {orderSignature && !orderSignature.isSignedByBuyer && (
                        <div className="flex justify-center mb-8">
                            <button
                                onClick={handleSignOrder}
                                disabled={signing || ncaLayerStatus !== 'available' || !selectedCertificate}
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                            >
                                {signing ? 'Подписание...' : 'Подписать договор'}
                            </button>
                        </div>
                    )}

                    {/* Сообщение об успешном подписании */}
                    {orderSignature?.isSignedByBuyer && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                            <div className="flex items-center">
                                <svg className="w-6 h-6 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <div>
                                    <p className="text-sm font-medium text-green-900">Заявка успешно подписана!</p>
                                    <p className="text-xs text-green-700 mt-1">
                                        {orderSignature.buyerSignedAt && `Подписано: ${new Date(orderSignature.buyerSignedAt).toLocaleString('ru-RU')}`}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Секция счёта на оплату - показываем когда подписана обеими сторонами */}
                    {orderSignature?.isSigned && (
                        <div className="bg-white shadow rounded-lg mb-8">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Счёт на оплату
                                </h3>
                            </div>
                            <div className="px-6 py-4">
                                {orderSignature.hasInvoice || orderSignature.invoicePdfPath ? (
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-green-600 font-medium">Счёт получен</span>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <button
                                                onClick={handleDownloadInvoice}
                                                className="flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                <span>Скачать счёт</span>
                                            </button>
                                            <button
                                                onClick={handleRefetchInvoice}
                                                disabled={loadingInvoice}
                                                className="flex items-center justify-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                                            >
                                                <svg className={`w-5 h-5 ${loadingInvoice ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                </svg>
                                                <span>Обновить</span>
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div className="flex items-center space-x-3">
                                            <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-orange-600 font-medium">Счёт ещё не получен</span>
                                        </div>
                                        <p className="text-sm text-gray-600">
                                            Счёт на оплату будет получен из системы 1С после подписания поставщиком.
                                        </p>
                                        <button
                                            onClick={handleRefetchInvoice}
                                            disabled={loadingInvoice}
                                            className="flex items-center justify-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                                        >
                                            <svg className={`w-5 h-5 ${loadingInvoice ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                            </svg>
                                            <span>{loadingInvoice ? 'Запрос...' : 'Запросить счёт'}</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Order Items */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
                    <h2 className="text-lg font-semibold text-gray-900">Позиции заказа</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Товар
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Код
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Количество
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Цена за ед.
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Сумма
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {order.items.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{item.productName}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-600">{item.productCode}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="text-sm text-gray-900">{item.quantity} {item.uom || 'шт'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="text-sm text-gray-900">{formatCurrency(item.unitPrice)}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <div className="text-sm font-medium text-gray-900">{formatCurrency(item.totalPrice)}</div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                            <tr>
                                <td colSpan={4} className="px-6 py-4 text-right text-sm font-semibold text-gray-900">
                                    Итого:
                                </td>
                                <td className="px-6 py-4 text-right text-base font-bold text-gray-900">
                                    {formatCurrency(order.totalAmount)}
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4">
                <button
                    onClick={() => navigate('/client/orders')}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                    Вернуться к списку
                </button>
            </div>
        </div>
    );
};

